import { useState } from "react";
import axios from "axios";
import { Loader2, Lock, RefreshCw } from "lucide-react";
import { useSeo } from "@/hooks/useSeo";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { API, COURSES, LEARNING_MODES, ROLES } from "@/lib/site";

const label = (list, v) => list.find((x) => x.value === v)?.label || v;
const courseName = (s) => COURSES.find((c) => c.slug === s)?.title || s;
const KEY = "mucci_admin_key";

const EnquiryTable = ({ rows }) => (
  <div className="card-soft overflow-x-auto" data-testid="enquiries-table">
    <Table>
      <TableHeader>
        <TableRow>
          {["Received", "Name", "Contact", "Who", "Year", "Courses", "Learning", "Message"].map((h) => <TableHead key={h} className="font-bold text-ink">{h}</TableHead>)}
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((r) => (
          <TableRow key={r.id} data-testid={`enquiry-row-${r.id}`}>
            <TableCell className="whitespace-nowrap">{new Date(r.created_at).toLocaleString("en-NZ")}</TableCell>
            <TableCell className="font-semibold">{r.name}</TableCell>
            <TableCell><a className="underline" href={`mailto:${r.email}`}>{r.email}</a>{r.phone && <div className="text-ink-muted">{r.phone}</div>}</TableCell>
            <TableCell>{label(ROLES, r.role)}</TableCell>
            <TableCell>{r.year_level}</TableCell>
            <TableCell>{r.interests.map(courseName).join(", ")}</TableCell>
            <TableCell>{label(LEARNING_MODES, r.learning_mode)}</TableCell>
            <TableCell className="max-w-xs whitespace-pre-wrap">{r.message || "—"}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </div>
);

export default function AdminEnquiries() {
  useSeo("Enquiries", "MUCCI Academy enquiries.");
  const [key, setKey] = useState(() => sessionStorage.getItem(KEY) || "");
  const [rows, setRows] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const load = async (e) => {
    e?.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.get(`${API}/enquiries`, { headers: { "X-Admin-Key": key } });
      sessionStorage.setItem(KEY, key);
      setRows(data);
    } catch (err) {
      setError(err.response?.status === 401 ? "That access key isn't right." : "Couldn't load enquiries. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="container-x py-14" data-testid="admin-page">
      <h1 className="text-4xl sm:text-5xl font-semibold">Enquiries</h1>
      {rows === null ? (
        <form onSubmit={load} className="mt-8 card-soft max-w-md p-8" data-testid="admin-key-form">
          <label htmlFor="admin-key" className="flex items-center gap-2 font-bold"><Lock className="h-4 w-4" aria-hidden="true" /> Access key</label>
          <Input id="admin-key" type="password" value={key} onChange={(e) => setKey(e.target.value)} className="mt-3 h-12 rounded-2xl border-2 border-ink/15" data-testid="admin-key-input" />
          {error && <p role="alert" className="mt-3 font-semibold text-destructive" data-testid="admin-error">{error}</p>}
          <button type="submit" disabled={!key || loading} className="mt-5 inline-flex items-center gap-2 rounded-full bg-ink px-6 py-3 font-bold text-cream disabled:opacity-60" data-testid="admin-key-submit">
            {loading && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />} View enquiries
          </button>
        </form>
      ) : (
        <div className="mt-8">
          <div className="mb-5 flex items-center justify-between">
            <p className="font-semibold" data-testid="enquiries-count">{rows.length} {rows.length === 1 ? "enquiry" : "enquiries"}</p>
            <button onClick={load} className="inline-flex items-center gap-2 rounded-full border border-ink/10 px-4 py-2 font-bold hover:bg-white" data-testid="admin-refresh">
              <RefreshCw className="h-4 w-4" aria-hidden="true" /> Refresh
            </button>
          </div>
          {rows.length ? <EnquiryTable rows={rows} /> : <p className="card-soft p-8" data-testid="enquiries-empty">No enquiries yet.</p>}
        </div>
      )}
    </section>
  );
}
